<template>
  <div :class="$style.view" v-if="previewPayload">
    <h1 :class="$style.view__header">この内容で送信しますか？</h1>
    <div
      :class="[$style.view__attention, $style.attention]"
      v-if="previewPayload.maybeOtherUserNames.length > 0"
    >
      <p :class="$style.attention__header">
        別のユーザーの名前が記入されている可能性があります。<br />
        {{
          REPLACE_NAME_VARIABLES.name
        }}と記述すると自動でユーザー名に変換されます。
      </p>
      <ul :class="$style.attention__list">
        <li
          v-for="(name, index) in previewPayload.maybeOtherUserNames"
          :key="index"
        >
          ・{{ name }}
        </li>
      </ul>
    </div>
    <p :class="$style.view__description">
      ユーザー1人につき1回しかアプローチできません。
      <br />後からの修正もできないので慎重に行いましょう。
    </p>
    <div :class="[$style.view__comment, $style.comment]">
      <p>{{ previewPayload.preview.comment }}</p>
      <ul
        v-if="plannerSelections.length > 0"
        :class="[$style.comment__selections, $style.selections]"
      >
        <li
          v-for="(selection, index) in plannerSelections"
          :key="index"
          :class="$style.selections__item"
        >
          <span>{{ selection }}</span>
        </li>
      </ul>
    </div>
    <FlatButton
      :class="$style.view__postButton"
      variant="primary"
      size="medium"
      @click="postComment"
      :disabled="isPostButtonDisabled"
    >
      この内容で送信する
    </FlatButton>
    <FlatButton
      :class="$style.view__editButton"
      variant="secondary"
      size="medium"
      @click="editComment"
    >
      修正する
    </FlatButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import type { KarteUser } from '@/types/app/'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  type PreviewKartePlannerCommentPreviewFragment,
  type PreviewKartePlannerCommentQuery,
  PreviewKartePlannerCommentDocument,
} from './KartePlannerCommentConfirmationView.generated'
import { REPLACE_NAME_VARIABLES } from '@/libs/utilities/replaceVariable'
import { useApolloClient } from '@vue/apollo-composable'

const props = withDefaults(
  defineProps<{
    plannerComment: string
    plannerSelections: string[]
    isPostButtonDisabled?: boolean
    karteUser: KarteUser
  }>(),
  {
    isPostButtonDisabled: false,
  }
)

const emit = defineEmits<{
  (event: 'post'): void
  (event: 'edit'): void
}>()

const postComment = () => {
  emit('post')
}
const editComment = () => {
  emit('edit')
}

const previewPayload = ref<PreviewKartePlannerCommentPreviewFragment>()

const karteIDString = props.karteUser.karte.id.toString()

const apollo = useApolloClient()

const fetchPreview = async () => {
  if (props.karteUser.karte.id === undefined) {
    throw new Error('karteId is undefined')
  }

  const { data } = await apollo.client.query<PreviewKartePlannerCommentQuery>({
    query: PreviewKartePlannerCommentDocument,
    variables: {
      karteID: karteIDString,
      comment: props.plannerComment,
      selections: props.plannerSelections,
    },
  })

  if (data.previewKartePlannerComment == null) {
    throw new Error('data is null!')
  }

  previewPayload.value = data.previewKartePlannerComment
}

onMounted(() => {
  fetchPreview()
})
</script>
<style lang="scss" module>
.view {
  display: flex;
  min-width: 400px;
  flex-basis: 800px;
  flex-direction: column;
  align-items: center;
  padding: 30px;

  &__header {
    color: $color-text-and-object-high-emphasis;
    font-size: 24px;
    font-weight: bold;
    line-height: 40px;
  }

  &__attention {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__description {
    color: $color-text-and-object-high-emphasis;
    font-size: 14px;
    line-height: 24px;

    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }

  &__comment {
    width: 100%;
    padding: 16px 24px;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__postButton {
    width: 240px;

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__editButton {
    width: 240px;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.attention {
  color: $color-caution-accent;
  font-size: 14px;

  &__header {
    font-weight: bold;
  }

  &__list {
    &:nth-child(n + 2) {
      margin-top: 16px;
    }
  }
}

.comment {
  background-color: $color-surface-secondary;
  border-radius: 6px;
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  line-height: 28px;
  white-space: pre-line;
  word-break: break-all;

  &__selections {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.selections {
  padding-left: 1em;

  &__item {
    list-style: circle;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}
</style>
